'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'Secure cloud architecture design',
    description:
      'Greenfield cloud architectures designed with security as a first-class requirement. Landing zones, account structures, network topology, and identity models built to resist compromise from day one.',
  },
  {
    tag: '[02]',
    title: 'Identity and access management hardening',
    description:
      'Least-privilege IAM across AWS, Azure, and GCP. Role-based access control, federated identity, privileged access management, and just-in-time access patterns that eliminate standing privilege.',
  },
  {
    tag: '[03]',
    title: 'Kubernetes and container security',
    description:
      'Container image scanning, runtime threat detection, pod security standards, network policies, and admission control. Kubernetes clusters hardened against the OWASP Kubernetes Top 10.',
  },
  {
    tag: '[04]',
    title: 'Cloud security posture management',
    description:
      'Continuous assessment of your cloud environment against CIS benchmarks, cloud provider security best practices, and your own policy baseline. Drift detected and remediated before it becomes an incident.',
  },
];

const outcomes = [
  { metric: 'CIS L2', label: 'Benchmark compliance across all cloud accounts' },
  { metric: 'Zero', label: 'Standing privileged access, JIT only' },
  { metric: '< 1h', label: 'Mean time to detect cloud misconfiguration' },
  { metric: '100%', label: 'Workload visibility across multi-cloud environments' },
];

const cloudProviders = [
  {
    provider: 'AWS',
    services: ['AWS Organizations & Control Tower', 'IAM Identity Center (SSO)', 'GuardDuty & Security Hub', 'AWS Config & CloudTrail', 'VPC & Network Firewall', 'EKS security hardening'],
  },
  {
    provider: 'Azure',
    services: ['Azure Landing Zones', 'Entra ID & Privileged Identity Management', 'Microsoft Defender for Cloud', 'Azure Policy & Blueprints', 'Azure Firewall & NSGs', 'AKS security hardening'],
  },
  {
    provider: 'GCP',
    services: ['GCP Organization Policy', 'Cloud IAM & Workload Identity', 'Security Command Center', 'VPC Service Controls', 'Cloud Armor', 'GKE security hardening'],
  },
];

export default function CloudPlatformPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '30%', opacity: 0.25 }} />
          <div className="orb orb-teal" style={{ width: '500px', height: '500px', bottom: '-100px', left: '-100px', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Cloud Platform Engineering</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Cloud & Platform Engineering]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Cloud platforms built<br />
              <span className="text-[#80edfa]">secure from the ground up.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] mb-10 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              We design and implement secure cloud architectures across AWS, Azure, and GCP, covering identity and access management, network segmentation, workload protection, and cloud-native security controls. Platform engineering that treats security as a first-class requirement, not an afterthought.
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up" style={{ transitionDelay: '0.2s' }}>
              <a href="#contact" className="btn-primary">Talk to a cloud security engineer</a>
              <a href="#providers" className="btn-secondary">Cloud provider coverage</a>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-[#222] py-16 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="bg-[#050a0f] px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Use cases</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                Cloud security that goes beyond the checkbox
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                Cloud misconfiguration remains the leading cause of cloud security incidents. TechSonar engineers design and implement cloud security controls that prevent misconfiguration, and detect it when it occurs.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Provider Coverage */}
      <section id="providers" className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Cloud provider coverage</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              AWS, Azure, and GCP, native security expertise across all three
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {cloudProviders?.map((cp, i) => (
              <div key={i} className="border border-[#222] bg-[#181b1f] overflow-hidden fade-in-up" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="h-1 w-full bg-gradient-to-r from-[#80edfa] to-transparent" />
                <div className="p-8">
                  <p className="font-heading text-2xl text-[#f5f5f8] font-medium mb-6">{cp?.provider}</p>
                  <ul className="flex flex-col gap-3">
                    {cp?.services?.map((s, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <img src="https://cdn.prod.website-files.com/68a77090b95f85ab69076143/68fba43b700c01c8947096af_checkbox.svg" alt="" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#aaa]">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kubernetes & Container Security */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            <div className="lg:w-1/2 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Kubernetes & container security</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
                Container security from image to runtime
              </h2>
              <p className="text-[#aaa] text-base leading-relaxed mb-8">
                Kubernetes is powerful and complex. Misconfigured clusters are a common attack vector. TechSonar engineers harden Kubernetes environments against the OWASP Kubernetes Top 10, from image supply chain to runtime threat detection.
              </p>
              <ul className="flex flex-col gap-4">
                {[
                  'Container image scanning and base image hardening',
                  'Pod Security Standards and admission control (OPA Gatekeeper)',
                  'Network policies and service mesh security (Istio, Linkerd)',
                  'Runtime threat detection (Falco, Sysdig)',
                  'Secrets management (Vault, External Secrets Operator)',
                  'RBAC hardening and least-privilege service accounts',
                ]?.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <img src="https://cdn.prod.website-files.com/68a77090b95f85ab69076143/68fba43b700c01c8947096af_checkbox.svg" alt="" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#aaa]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:w-1/2 flex flex-col gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Zero-trust network architecture</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  Network segmentation, micro-segmentation, and zero-trust network access across cloud and on-premise. Lateral movement is constrained by design, not by hope. Every workload communicates only with what it needs to, verified by identity.
                </p>
              </div>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Cloud security posture management</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  Continuous assessment against CIS benchmarks, cloud provider security best practices, and your own policy baseline. GuardDuty, Defender for Cloud, and Security Command Center configured and tuned, not just enabled. Findings triaged by risk, not just severity.
                </p>
              </div>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Multi-cloud expertise</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  Most organisations operate across multiple cloud providers. TechSonar engineers hold certifications across AWS, Azure, and GCP, and understand the security implications of multi-cloud architectures, not just single-provider deployments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Build your cloud platform secure from the start.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <a href="#contact" className="btn-primary">Book a scoping call</a>
            <Link href="/services" className="btn-secondary">View all services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
